/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Termin } from '@/domain/termine/shared/types/Termin';
import {
  faBarsSort,
  faCalendarStar,
  faCircleInfo,
  faClock,
  faArrowsRepeat,
  faLink,
} from '@fortawesome/pro-regular-svg-icons';
import {
  getWiederholungStringFromTerminWiederholung,
  TerminWiederholung,
} from '@/domain/termine/shared/types/TerminWiederholung';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TerminImage } from '@/domain/termine/components/TerminImage';
import { TerminInfoRow } from '@/domain/termine/features/Calendar/shared/TerminInfo/TerminInfoRow';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { TerminVerknuepfungArbeitszeit } from '@/domain/termine/features/Calendar/shared/TerminInfo/Verknuepfungen/TerminVerknuepfungArbeitszeit';
import { formatTerminDateTime } from '@/domain/termine/shared/utils/formatDateTime';
import { TerminAccordion } from '@/domain/termine/features/Calendar/shared/TerminInfo/Verknuepfungen/TerminAccordion';
import { TerminVerknuepfungSchicht } from '@/domain/termine/features/Calendar/shared/TerminInfo/Verknuepfungen/TerminVerknuepfungSchicht';

export function TerminInfo({ termin }: { termin: Termin }) {
  const { t } = useTranslation();
  const { kalender } = useCalendarContext();

  const matchingKalender = kalender?.find(
    (k) => k.id === termin.idTerminkalender,
  );

  const matchingArbeitszeiten = termin.arbeitszeiten ?? [];
  const matchingSchichten = termin.schichten ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-neutrals-900 flex-1">
        <TerminImage
          bildUrl={termin.image?.url}
          bildName={termin.image?.name}
          markierung={termin.markierung}
          variant="detail"
        />
        <div
          className="font-semibold text-xl line-clamp-2 min-w-0 text-start"
          title={termin.titel}>
          {termin.titel}
        </div>
      </div>
      <div className="grid grid-cols-[1fr] gap-3 overflow-y-auto items-center text-neutrals-900">
        <TerminInfoRow icon={faClock}>
          {formatTerminDateTime(termin.datum, termin.beginn, termin.ende)}
        </TerminInfoRow>
        {termin.wiederholung &&
          termin.wiederholung !== TerminWiederholung.OHNE && (
            <TerminInfoRow icon={faArrowsRepeat}>
              {t('domain.termine.repeat', {
                wiederholung: termin.wiederholung,
              })}{' '}
              {getWiederholungStringFromTerminWiederholung(
                termin.wiederholung,
              ).toLowerCase()}{' '}
              {termin.wiederholungBis &&
                dayjs(termin.wiederholungBis).format('DD.MM.YYYY')}
            </TerminInfoRow>
          )}
        {termin.beschreibung && (
          <TerminInfoRow icon={faBarsSort}>
            <p className="line-clamp-3 leading-5" title={termin.beschreibung}>
              {termin.beschreibung}
            </p>
          </TerminInfoRow>
        )}
        {termin.specials && (
          <TerminInfoRow icon={faCircleInfo}>
            <p className="line-clamp-3 leading-5" title={termin.specials}>
              {termin.specials}
            </p>
          </TerminInfoRow>
        )}
        {(matchingArbeitszeiten.length > 0 ||
          termin.hasHiddenArbeitszeiten) && (
          <TerminInfoRow icon={faLink}>
            <TerminAccordion
              id={termin.id}
              triggerLabel={t('domain.termine.linkedArbeitszeiten', {
                count: matchingArbeitszeiten.length,
              })}
              count={matchingArbeitszeiten.length}>
              {matchingArbeitszeiten.length >= 0 && (
                <TerminVerknuepfungArbeitszeit
                  arbeitszeiten={matchingArbeitszeiten}
                  hiddenArbeitsbereich={termin.hasHiddenArbeitszeiten}
                />
              )}
            </TerminAccordion>
          </TerminInfoRow>
        )}
        {(matchingSchichten.length > 0 || termin.hasHiddenSchichten) && (
          <TerminInfoRow icon={faLink}>
            <TerminAccordion
              id={termin.id}
              triggerLabel={t('domain.termine.linkedSchichten', {
                count: matchingSchichten.length,
              })}
              count={matchingSchichten.length}>
              {matchingSchichten.length >= 0 && (
                <TerminVerknuepfungSchicht
                  schichten={matchingSchichten}
                  hiddenArbeitsbereich={termin.hasHiddenSchichten}
                />
              )}
            </TerminAccordion>
          </TerminInfoRow>
        )}
        {matchingKalender && (
          <TerminInfoRow icon={faCalendarStar}>
            {matchingKalender.name}
          </TerminInfoRow>
        )}
      </div>
    </div>
  );
}
